import { type Ref, forwardRef } from 'react';
import { type FieldValues, type UseControllerProps, useController } from 'react-hook-form';

import getFieldError from '@/shared/form/helpers/getFieldError';
import useMapCustomValidators from '@/shared/form/hooks/useMapCustomValidators';
import type { InputProps } from '@/shared/form/types';

import { TimeInput as MantineTimeInput, type TimeInputProps as MantineTimeInputProps } from '@mantine/dates';
import { useMergedRef } from '@mantine/hooks';

export type TimeInputProps<T extends FieldValues> = InputProps<UseControllerProps<T>> &
  Omit<MantineTimeInputProps, 'value' | 'defaultValue'>;

export default forwardRef(function TimeInput<T extends FieldValues>(
  { name, control, defaultValue, shouldUnregister, onChange, ...props }: TimeInputProps<T>,
  ref: Ref<HTMLInputElement>,
) {
  const rules = useMapCustomValidators(props, true);

  const {
    field: { value, onChange: fieldOnChange, ref: insideRef, ...field },
    fieldState,
  } = useController<T>({
    name,
    control,
    defaultValue,
    rules,
    shouldUnregister,
  });

  const mergedRefs = useMergedRef(insideRef, ref);

  return (
    <MantineTimeInput
      ref={mergedRefs}
      error={getFieldError(fieldState)}
      value={value}
      onChange={e => {
        fieldOnChange(e);
        onChange?.(name, e.currentTarget.value);
      }}
      data-testid={name}
      {...field}
      {...props}
    />
  );
});
